import { CLIENT_CREDIT_CARD_FORM_PATH, CLIENT_PROFILE_FORM_PATH, LOGIN_REDIRECT_PATH } from '@/consts/paths';
import type { Nullable } from '@/types/common';

const CLIENT_PROFILE_FORM_TOKEN_KEY = 'clientProfileFormToken';
export const LINK_TOKEN_QUERY_KEY = 'token';

const API_PREFIX = '/api/';
const PUBLIC_API_SEGMENTS = new Set(['public', 'worldwide', 'maintenance']);
const SEND_FORMS_PATHS = new Set<string>([CLIENT_PROFILE_FORM_PATH, CLIENT_CREDIT_CARD_FORM_PATH]);

function trimSlashes(value: string): string {
  return value.replace(/^\/+|\/+$/g, '');
}

function getSlotKey(): string {
  const linkToken = new URLSearchParams(window.location.search).get(LINK_TOKEN_QUERY_KEY) ?? '';
  return `${CLIENT_PROFILE_FORM_TOKEN_KEY}:${linkToken}`;
}

/**
 * Stores the Client Profile form token per Send Forms link.
 *
 * @remarks
 * Keyed by the link's `token` query param, which `/login-redirect/` and the form both keep, so two links open in one
 * browser never read each other's token.
 */
export const clientProfileFormSession = {
  save(token: string): void {
    localStorage.setItem(getSlotKey(), token);
  },
  readToken(): Nullable<string> {
    return localStorage.getItem(getSlotKey());
  },
  clear(): void {
    localStorage.removeItem(getSlotKey());
  },
};

export function buildLoginRedirectPath({ pathname, search }: Pick<Location, 'pathname' | 'search'>): string {
  const query = new URLSearchParams(search);
  query.set('route', trimSlashes(pathname));
  return `${LOGIN_REDIRECT_PATH}?${query.toString()}`;
}

/** Returns the Send Forms page a `route` names, or `null` for any other value, so a crafted one cannot leave the app. */
export function toSendFormsPath(route: Nullable<string>): Nullable<string> {
  if (!route) {
    return null;
  }

  const path = `/${trimSlashes(route)}/`;
  return SEND_FORMS_PATHS.has(path) ? path : null;
}

export function toShareApiPath(path: string): string {
  if (!path.startsWith(API_PREFIX)) {
    return path;
  }

  const apiPath = path.slice(API_PREFIX.length);
  const firstSegment = apiPath.split(/[/?]/)[0];

  if (PUBLIC_API_SEGMENTS.has(firstSegment)) {
    return path;
  }

  return `${API_PREFIX}share/${apiPath}`;
}
